import { AlertTriangle } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { cn } from '@/lib/utils';
import { encounterTypeColors } from '@/features/emr';
import type { CalendarEntry } from '../api/calendar-api';

/**
 * One appointment in a day cell.
 *
 * The conflict marker appears when the appointment instant falls on a different
 * calendar day in the practice's timezone than the encounter is dated against.
 * That is reachable — an administrator changing the practice zone, or a client
 * patching `scheduled_at` alone — and when it happens the chip stays in the cell
 * its `encounter_date` chose. Moving it would be the app deciding a clinical
 * fact it does not know.
 */
export function EncounterChip({ entry, className }: { className?: string; entry: CalendarEntry }) {
  const name = entry.patient?.name?.trim() || entry.title?.trim() || 'Unassigned';
  const conflicted = entry.scheduled_local_date !== null && entry.scheduled_local_date !== entry.encounter_date;

  return (
    <Link
      to={entry.imaging_study_id ? '/imaging/$studyId' : '/encounters/$encounterId'}
      params={entry.imaging_study_id ? { studyId: entry.imaging_study_id } : { encounterId: entry.id }}
      title={
        conflicted
          ? `Scheduled ${entry.scheduled_local_time} on ${entry.scheduled_local_date} in the practice timezone, but recorded against ${entry.encounter_date}.`
          : `${entry.scheduled_local_time ?? 'No time'} · ${name}`
      }
      className={cn(
        'flex w-full items-center gap-1 rounded-md px-1.5 py-1 text-left text-[11px] leading-tight transition-opacity hover:opacity-80',
        encounterTypeColors[entry.encounter_type ?? ''] ?? 'bg-muted text-muted-foreground',
        className,
      )}>
      {conflicted && <AlertTriangle aria-hidden="true" className="size-3 shrink-0" />}
      <span className="shrink-0 tabular-nums opacity-70">{entry.scheduled_local_time ?? '—'}</span>
      <span className="truncate">{entry.imaging_study_id ? `${name} · ${entry.description || 'Imaging'}` : name}</span>
    </Link>
  );
}
